import { createModuleContext, DEPLOYMENT_CHANNEL, isCurveRef, listArtisModuleTypes, serverNow, UNSYNCED_CLOCK_STATE, } from "@artis/sdk";
const HELP_TEXT = "[artis] commands (every on<Name>(cb?) logs now and on every change, and returns an unsubscribe fn):\n" +
    "  artis.debug.version()                   app and worker SDK versions\n" +
    '  artis.debug.find(query)                 search products by name or description (case-insensitive; "" lists all)\n' +
    "  artis.debug.products()                  the product catalog; returns the list\n" +
    "  artis.debug.onProducts(cb?)             catalog deltas as +upserts -removes\n" +
    "  artis.debug.watch(id, offsets?, name?)  subscribe to a curve; returns an unsubscribe fn\n" +
    "  artis.debug.watchSeries(id, dates?, name?) subscribe to a settlement series ({ from, to } window); returns an unsubscribe fn\n" +
    "  artis.debug.watchSummary(id, name?)     subscribe to a series' summary tables; returns an unsubscribe fn\n" +
    "  artis.debug.onSelect(cb?)               grid selections as they happen\n" +
    "  artis.debug.eod()                       the EOD date and Prior date; returns the snapshot\n" +
    "  artis.debug.onEod(cb?)                  the EOD date and Prior date\n" +
    "  artis.debug.panes()                     the current panes; returns the pane list\n" +
    "  artis.debug.onPanes(cb?)                the panes\n" +
    '  artis.debug.split(paneId, orientation?) split a pane ("vertical" default); use a panes() id\n' +
    "  artis.debug.close(paneId)               close a pane by id\n" +
    "  artis.debug.offset(offset)              check an offset: canonical, absolute and relative spelling, base month\n" +
    "  artis.debug.time()                      server clock: skew, uncertainty, last sync; returns the state\n" +
    "  artis.debug.onTime(cb?)                 the server clock, on every re-sync\n" +
    "  artis.debug.deployment()                loaded vs newest deployed version; returns the state\n" +
    "  artis.debug.onDeployment(cb?)           the deployment state\n" +
    "  artis.debug.simulateUpdate(version?)    broadcast a fake deployed version so every window acts as on a real deploy\n" +
    '  artis.debug.storage(module, scope?)     a module\'s storage ("browser" default): get/set/remove/subscribe(key, cb?)\n' +
    "  artis.debug.pages()                     the user's pages sorted by order; returns the list (starts the worker's pages source)\n" +
    "  artis.debug.onPages(cb?)                page deltas as +upserts -removes\n" +
    "  artis.debug.columns(pageId)             a page's columns sorted by order; returns the list\n" +
    "  artis.debug.onColumns(pageId, cb?)      column deltas of a page as +upserts -removes\n" +
    "  artis.debug.help()                      this message";
export const setupConsole = (artis, target, options = {}) => {
    const logger = options.logger ?? console;
    const active = new Set();
    /** A stream helper: `cb` replaces the log line; the subscription ends with the returned fn or the console cleanup. */
    const listen = (subscribe, log, cb) => {
        const handle = { dispose: subscribe(cb ?? log) };
        active.add(handle);
        return () => {
            handle.dispose();
            active.delete(handle);
        };
    };
    /** Both helpers of a state API, sharing one log line: `current` for the one-shot, `on` for the stream. */
    const channel = (subscribe, empty, log, logEmpty) => {
        let last;
        active.add({
            dispose: subscribe((value) => {
                last = value;
            }),
        });
        return {
            current: () => {
                if (last === undefined) {
                    logEmpty();
                    return empty;
                }
                log(last);
                return last;
            },
            on: (cb) => listen(subscribe, log, cb),
        };
    };
    const logClock = (state) => {
        const serverTime = serverNow(state);
        if (serverTime === null)
            return logger.log("[artis] server clock: not synced yet");
        const skew = serverTime - Date.now();
        const sign = skew >= 0 ? "+" : "";
        const direction = skew >= 0 ? "local clock is behind the server" : "local clock is ahead of the server";
        const ageSec = state.lastSyncAt === null ? null : Math.max(0, Math.round((serverTime - state.lastSyncAt) / 1_000));
        logger.log("[artis] server clock\n" +
            `  server time  ${new Date(serverTime).toISOString()}\n` +
            `  local time   ${new Date(Date.now()).toISOString()}\n` +
            `  skew         ${sign}${skew}ms (${direction})\n` +
            `  uncertainty  ±${state.uncertaintyMs}ms\n` +
            `  last sync    ${ageSec === null ? "unknown" : `${ageSec}s ago`}`);
    };
    const eod = channel(artis.v1.eod.subscribe, null, (snapshot) => logger.log(`[artis] eod date ${snapshot.date ?? "none"}, prior ${snapshot.priorDate ?? "none"}`, snapshot), () => logger.log("[artis] eod: dates not resolved yet"));
    const logPanes = (panes) => logger.log(`[artis] panes (${panes.length})`, panes.map((p) => p.id));
    const NO_PANES = [];
    const panes = channel(artis.v1.layout.subscribe, NO_PANES, logPanes, () => logPanes(NO_PANES));
    const clock = channel(artis.v1.time.subscribe, UNSYNCED_CLOCK_STATE, logClock, () => logClock(UNSYNCED_CLOCK_STATE));
    const logDeployment = (state) => logger.log("[artis] deployment\n" +
        `  current   ${state.current || "unknown"}\n` +
        `  latest    ${state.latest ?? "not checked yet"}\n` +
        `  outdated  ${state.isOutdated}`);
    const NO_DEPLOYMENT = { current: "", latest: undefined, isOutdated: false };
    const deployment = channel(artis.v1.deployment.subscribe, NO_DEPLOYMENT, logDeployment, () => logDeployment(NO_DEPLOYMENT));
    const byOrder = (a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0);
    /** Folds a delta stream into a snapshot, subscribing on first read so an unused helper never binds the channel. */
    const lazyFold = (subscribe) => {
        const byId = new Map();
        let started = false;
        return () => {
            if (!started) {
                started = true;
                active.add({
                    dispose: subscribe((delta) => {
                        for (const row of delta.upserts ?? [])
                            byId.set(row.id, row);
                        for (const id of delta.removes ?? [])
                            byId.delete(id);
                    }),
                });
            }
            return [...byId.values()].sort(byOrder);
        };
    };
    const readPages = lazyFold((cb) => artis.v1.pages.subscribe(cb));
    const columnReaders = new Map();
    const readColumns = (pageId) => {
        let reader = columnReaders.get(pageId);
        if (!reader) {
            reader = lazyFold((cb) => artis.v1.pages.columns.subscribe(pageId, cb));
            columnReaders.set(pageId, reader);
        }
        return reader();
    };
    const logDelta = (what, delta) => logger.log(`[artis] ${what} +${delta.upserts?.length ?? 0} -${delta.removes?.length ?? 0}`, delta);
    const catalog = new Map();
    const fieldFor = (id) => catalog.get(id)?.defaultField ?? "value";
    const watchRef = (ref) => {
        const watcher = artis.v1.createWatcher((updates) => {
            for (const [r, values] of updates) {
                logger.log(`[artis] ${r.name} (${r.id})`, values);
            }
        });
        watcher.add(ref);
        const handle = { dispose: watcher.dispose };
        active.add(handle);
        return () => {
            watcher.dispose();
            active.delete(handle);
        };
    };
    active.add({
        dispose: artis.v1.products.subscribe((delta) => {
            for (const product of delta.upserts ?? [])
                catalog.set(product.id, product);
            for (const id of delta.removes ?? [])
                catalog.delete(id);
        }),
    });
    const debug = {
        version: () => {
            const version = artis.v1.version;
            logger.log(`[artis] version app ${version.app}, worker ${version.worker}`);
            return version;
        },
        find: (query) => {
            if (catalog.size === 0) {
                logger.log("[artis] product catalog not loaded yet");
                return [];
            }
            const q = query.toLowerCase();
            const results = [];
            for (const entry of catalog.values()) {
                const nameMatch = entry.name.toLowerCase().includes(q);
                const descMatch = entry.description?.toLowerCase().includes(q) ?? false;
                if (nameMatch || descMatch)
                    results.push(entry);
            }
            return results;
        },
        products: () => {
            logger.log(`[artis] products (${catalog.size})`);
            return [...catalog.values()];
        },
        onProducts: (cb) => listen(artis.v1.products.subscribe, (delta) => logger.log(`[artis] products +${delta.upserts?.length ?? 0} -${delta.removes?.length ?? 0}`, delta), cb),
        watch: (id, offsets, name) => watchRef({ id, name: name ?? id, field: fieldFor(id), offsets }),
        watchSeries: (id, dates, name) => watchRef({ kind: "series", id, name: name ?? id, field: fieldFor(id), dates }),
        watchSummary: (id, name) => watchRef({ kind: "summary", id, name: name ?? id }),
        onSelect: (cb) => listen(artis.v1.grid.selection.subscribe, (selection) => logger.log(`[artis] selection (${selection.length})`, selection.map((r) => `${r.name} (${isCurveRef(r) ? r.id : "period"})`)), cb),
        eod: eod.current,
        onEod: eod.on,
        panes: panes.current,
        onPanes: panes.on,
        split: (paneId, orientation = "vertical") => {
            artis.v1.layout.split(paneId, orientation);
            logger.log(`[artis] split ${paneId} ${orientation}`);
        },
        close: (paneId) => {
            artis.v1.layout.close(paneId);
            logger.log(`[artis] close ${paneId}`);
        },
        offset: (offset) => {
            const { validate, toAbsolute, toRelative, getBaseMonth } = artis.v1.offsets;
            const result = {
                validation: validate(offset),
                absolute: toAbsolute(offset),
                relative: toRelative(offset),
                baseMonth: getBaseMonth(),
            };
            const show = (v) => v.normalized ?? `error: ${v.error}`;
            logger.log(`[artis] offset ${offset}\n` +
                `  canonical   ${show(result.validation)}\n` +
                `  absolute    ${show(result.absolute)}\n` +
                `  relative    ${show(result.relative)}\n` +
                `  base month  ${result.baseMonth}`);
            return result;
        },
        time: clock.current,
        onTime: clock.on,
        deployment: deployment.current,
        onDeployment: deployment.on,
        simulateUpdate: (version = "debug-simulated") => {
            const broadcast = new BroadcastChannel(DEPLOYMENT_CHANNEL);
            broadcast.postMessage({ type: "latest", version });
            broadcast.close();
            logger.log(`[artis] broadcast simulated deployment "${version}"; reload to reset`);
        },
        storage: (module, scope = "browser") => {
            if (!listArtisModuleTypes().includes(module)) {
                logger.log(`[artis] note: "${module}" is not a registered module type`);
            }
            const store = createModuleContext(module).storage(scope);
            return {
                get: (key) => store.get(key),
                set: (key, value) => store.set(key, value),
                remove: (key) => store.remove(key),
                subscribe: (key, cb) => listen((onChange) => store.subscribe(key, onChange), (value) => logger.log(`[artis] storage ${module}/${scope} ${key}`, value), cb),
            };
        },
        pages: () => {
            const pages = readPages();
            logger.log(`[artis] pages (${pages.length})`, pages);
            return pages;
        },
        onPages: (cb) => listen(artis.v1.pages.subscribe, (delta) => logDelta("pages", delta), cb),
        columns: (pageId) => {
            const columns = readColumns(pageId);
            logger.log(`[artis] columns of ${pageId} (${columns.length})`, columns);
            return columns;
        },
        onColumns: (pageId, cb) => listen((next) => artis.v1.pages.columns.subscribe(pageId, next), (delta) => logDelta(`columns of ${pageId}`, delta), cb),
        help: () => logger.log(HELP_TEXT),
    };
    target.artis = Object.create(artis, {
        debug: { value: debug, enumerable: true },
    });
    return () => {
        for (const handle of active)
            handle.dispose();
        active.clear();
        target.artis = undefined;
    };
};
//# sourceMappingURL=setupConsole.js.map